import { useCallback, useEffect, useMemo, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createReadingAssignment,
  createReadingRoute,
  deleteReadingAssignment,
  deleteReadingRoute,
  listContracts,
  listCustomers,
  listMeterReadings,
  listMobileDevices,
  listReadingAssignments,
  listReadingPeriods,
  listReadingRoutes,
  listSectors,
  releaseMeterReading,
  updateReadingRoute,
  type ContractListItem,
  type CustomerListItem,
  type MeterReadingItem,
  type MobileDeviceItem,
  type ReadingAssignmentItem,
  type ReadingPeriodItem,
  type ReadingRouteItem,
  type SectorItem,
} from "@/lib/api";

type StatusFilter = "todos" | "pendientes" | "tomadas";
type TabKey = "asignacion" | "rutas";

type RouteDraft = {
  id: string | null;
  code: string;
  name: string;
  active: boolean;
  sectorIds: string[];
  contractIds: string[];
};

const emptyRouteDraft: RouteDraft = {
  id: null,
  code: "",
  name: "",
  active: true,
  sectorIds: [],
  contractIds: [],
};

function sortContracts(items: ContractListItem[], customersById: Map<string, CustomerListItem>): ContractListItem[] {
  return items.slice().sort((a, b) => {
    const sectorCompare = a.sector.localeCompare(b.sector);
    if (sectorCompare !== 0) return sectorCompare;
    const customerA = customersById.get(a.customerId)?.nombre ?? "";
    const customerB = customersById.get(b.customerId)?.nombre ?? "";
    const customerCompare = customerA.localeCompare(customerB);
    if (customerCompare !== 0) return customerCompare;
    return a.contractNumber.localeCompare(b.contractNumber);
  });
}

export default function ControlLecturasPage() {
  const { session } = useSession();
  const [activeTab, setActiveTab] = useState<TabKey>("asignacion");
  const [periods, setPeriods] = useState<ReadingPeriodItem[]>([]);
  const [sectors, setSectors] = useState<SectorItem[]>([]);
  const [routes, setRoutes] = useState<ReadingRouteItem[]>([]);
  const [devices, setDevices] = useState<MobileDeviceItem[]>([]);
  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [assignments, setAssignments] = useState<ReadingAssignmentItem[]>([]);
  const [readings, setReadings] = useState<MeterReadingItem[]>([]);

  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [selectedDeviceCode, setSelectedDeviceCode] = useState("");
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [filterRoute, setFilterRoute] = useState("todos");
  const [filterStatus, setFilterStatus] = useState<StatusFilter>("todos");
  const [search, setSearch] = useState("");
  const [routeSearch, setRouteSearch] = useState("");
  const [routeDraft, setRouteDraft] = useState<RouteDraft>(emptyRouteDraft);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingRoute, setSavingRoute] = useState(false);
  const [deletingRouteId, setDeletingRouteId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [releasingContractId, setReleasingContractId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [draggingContractId, setDraggingContractId] = useState<string | null>(null);

  const selectedPeriod = useMemo(
    () => periods.find((period) => period.id === selectedPeriodId) ?? null,
    [periods, selectedPeriodId],
  );
  const isSelectedPeriodClosed = selectedPeriod?.status === "closed";

  const customersById = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers]);
  const sectorsById = useMemo(() => new Map(sectors.map((sector) => [sector.id, sector])), [sectors]);
  const routesById = useMemo(() => new Map(routes.map((route) => [route.id, route])), [routes]);

  const loadBase = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const [p, s, r, d, c, cu] = await Promise.all([
        listReadingPeriods(session),
        listSectors(session),
        listReadingRoutes(session),
        listMobileDevices(session),
        listContracts(session),
        listCustomers(session),
      ]);
      setPeriods(p);
      setSectors(s);
      setRoutes(r);
      setDevices(d);
      setContracts(c);
      setCustomers(cu);
      const openPeriod = p.find((row) => row.status === "open") ?? p[0];
      setSelectedPeriodId((current) => current || openPeriod?.id || "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron cargar datos base");
    } finally {
      setLoading(false);
    }
  }, [session]);

  const refreshOperational = useCallback(async () => {
    if (!session || !selectedPeriodId) return;
    setRefreshing(true);
    try {
      const [a, r] = await Promise.all([
        listReadingAssignments(session, selectedPeriodId),
        listMeterReadings(session, selectedPeriodId),
      ]);
      setAssignments(a);
      setReadings(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo cargar control de lecturas");
    } finally {
      setRefreshing(false);
    }
  }, [selectedPeriodId, session]);

  useEffect(() => {
    void loadBase();
  }, [loadBase]);

  useEffect(() => {
    void refreshOperational();
  }, [refreshOperational]);

  useEffect(() => {
    if (!session || !selectedPeriodId) return;
    const timer = setInterval(() => {
      void refreshOperational();
    }, 10000);
    return () => clearInterval(timer);
  }, [refreshOperational, selectedPeriodId, session]);

  const selectedDeviceAssignments = useMemo(
    () =>
      assignments.filter(
        (assignment) =>
          (!selectedDeviceCode || assignment.assignedDeviceCode === selectedDeviceCode) &&
          (!selectedPeriodId || assignment.readingPeriodId === selectedPeriodId),
      ),
    [assignments, selectedDeviceCode, selectedPeriodId],
  );

  useEffect(() => {
    if (!selectedDeviceCode || !selectedPeriodId) {
      setSelectedRouteIds([]);
      return;
    }
    setSelectedRouteIds(
      Array.from(
        new Set(
          assignments
            .filter(
              (assignment) =>
                assignment.assignedDeviceCode === selectedDeviceCode &&
                assignment.readingPeriodId === selectedPeriodId &&
                assignment.routeId,
            )
            .map((assignment) => assignment.routeId),
        ),
      ),
    );
  }, [assignments, selectedDeviceCode, selectedPeriodId]);

  const routeCoverageEntries = useMemo(() => {
    const seen = new Set<string>();
    const items: Array<{ contract: ContractListItem; routeLabel: string }> = [];

    for (const assignment of selectedDeviceAssignments) {
      if (assignment.routeId) {
        const route = routesById.get(assignment.routeId);
        if (!route) continue;
        const routeContracts =
          route.contractIds.length > 0
            ? route.contractIds
                .map((contractId) => contracts.find((contract) => contract.id === contractId))
                .filter((contract): contract is ContractListItem => Boolean(contract))
            : sortContracts(
                contracts.filter(
                  (contract) =>
                    Boolean(contract.sectorId) && route.sectorIds.includes(contract.sectorId ?? ""),
                ),
                customersById,
              );

        for (const contract of routeContracts) {
          if (seen.has(contract.id)) continue;
          seen.add(contract.id);
          items.push({ contract, routeLabel: route.name });
        }
        continue;
      }

      const sectorContracts = sortContracts(
        contracts.filter(
          (contract) =>
            (assignment.sectorId && contract.sectorId === assignment.sectorId) || contract.sector === assignment.sectorName,
        ),
        customersById,
      );

      for (const contract of sectorContracts) {
        if (seen.has(contract.id)) continue;
        seen.add(contract.id);
        items.push({ contract, routeLabel: assignment.sectorName });
      }
    }

    return items;
  }, [contracts, customersById, routesById, selectedDeviceAssignments]);

  const takenContractIds = useMemo(() => new Set(readings.map((reading) => reading.contractId)), [readings]);

  const monitorRows = useMemo(() => {
    return routeCoverageEntries
      .map(({ contract, routeLabel }) => {
        const customer = customersById.get(contract.customerId);
        const status: StatusFilter = takenContractIds.has(contract.id) ? "tomadas" : "pendientes";
        return {
          contractId: contract.id,
          contractNumber: contract.contractNumber,
          sector: contract.sector,
          routeLabel,
          customerName: customer?.nombre ?? "Sin cliente",
          customerRut: customer?.rut ?? "-",
          customerAddress: customer?.direccion ?? contract.startAddress,
          status,
        };
      })
      .filter((row) => {
        const byRoute = filterRoute === "todos" || row.routeLabel === filterRoute;
        const byStatus = filterStatus === "todos" || row.status === filterStatus;
        const term = search.trim().toLowerCase();
        const bySearch =
          term.length === 0 ||
          row.customerName.toLowerCase().includes(term) ||
          row.customerRut.toLowerCase().includes(term) ||
          row.customerAddress.toLowerCase().includes(term) ||
          row.contractNumber.toLowerCase().includes(term);
        return byRoute && byStatus && bySearch;
      });
  }, [customersById, filterRoute, filterStatus, routeCoverageEntries, search, takenContractIds]);

  const tomadasCount = monitorRows.filter((row) => row.status === "tomadas").length;
  const pendientesCount = monitorRows.filter((row) => row.status === "pendientes").length;
  const assignedRouteNames = useMemo(
    () =>
      Array.from(
        new Set(
          selectedDeviceAssignments
            .map((assignment) => assignment.routeName || assignment.sectorName)
            .filter(Boolean),
        ),
      ),
    [selectedDeviceAssignments],
  );

  const filteredRoutes = useMemo(() => {
    const term = routeSearch.trim().toLowerCase();
    return routes.filter((route) => {
      if (!term) return true;
      return route.name.toLowerCase().includes(term) || route.code.toLowerCase().includes(term);
    });
  }, [routeSearch, routes]);

  const selectedRouteContracts = useMemo(() => {
    const selected = routeDraft.contractIds
      .map((contractId) => contracts.find((contract) => contract.id === contractId))
      .filter((contract): contract is ContractListItem => Boolean(contract));
    return sortContracts(selected, customersById).sort(
      (a, b) => routeDraft.contractIds.indexOf(a.id) - routeDraft.contractIds.indexOf(b.id),
    );
  }, [contracts, customersById, routeDraft.contractIds]);

  const availableRouteContracts = useMemo(() => {
    const selectedSectorNames = routeDraft.sectorIds
      .map((sectorId) => sectorsById.get(sectorId)?.name ?? "")
      .filter(Boolean);
    const base = contracts.filter(
      (contract) =>
        ((Boolean(contract.sectorId) && routeDraft.sectorIds.includes(contract.sectorId ?? "")) ||
          selectedSectorNames.includes(contract.sector)) &&
        !routeDraft.contractIds.includes(contract.id),
    );
    return sortContracts(base, customersById);
  }, [contracts, customersById, routeDraft.contractIds, routeDraft.sectorIds, sectorsById]);

  function resetRouteDraft(): void {
    setRouteDraft(emptyRouteDraft);
  }

  function startEditRoute(route: ReadingRouteItem): void {
    setRouteDraft({
      id: route.id,
      code: route.code,
      name: route.name,
      active: route.active,
      sectorIds: route.sectorIds,
      contractIds: route.contractIds,
    });
    setActiveTab("rutas");
  }

  function toggleRouteSector(sectorId: string): void {
    setRouteDraft((current) => {
      const sectorIds = current.sectorIds.includes(sectorId)
        ? current.sectorIds.filter((id) => id !== sectorId)
        : [...current.sectorIds, sectorId];
      const sectorNames = sectorIds.map((id) => sectorsById.get(id)?.name ?? "").filter(Boolean);
      const allowedContractIds = new Set(
        contracts
          .filter(
            (contract) =>
              (Boolean(contract.sectorId) && sectorIds.includes(contract.sectorId ?? "")) ||
              sectorNames.includes(contract.sector),
          )
          .map((contract) => contract.id),
      );
      return {
        ...current,
        sectorIds,
        contractIds: current.contractIds.filter((contractId) => allowedContractIds.has(contractId)),
      };
    });
  }

  function addContractToRoute(contractId: string): void {
    setRouteDraft((current) =>
      current.contractIds.includes(contractId)
        ? current
        : { ...current, contractIds: [...current.contractIds, contractId] },
    );
  }

  function removeContractFromRoute(contractId: string): void {
    setRouteDraft((current) => ({
      ...current,
      contractIds: current.contractIds.filter((id) => id !== contractId),
    }));
  }

  function reorderRouteContract(sourceContractId: string, targetContractId: string): void {
    if (sourceContractId === targetContractId) return;
    setRouteDraft((current) => {
      const next = current.contractIds.slice();
      const sourceIndex = next.indexOf(sourceContractId);
      const targetIndex = next.indexOf(targetContractId);
      if (sourceIndex < 0 || targetIndex < 0) return current;
      next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, sourceContractId);
      return { ...current, contractIds: next };
    });
  }

  function toggleSelectedRoute(routeId: string): void {
    setSelectedRouteIds((current) =>
      current.includes(routeId) ? current.filter((id) => id !== routeId) : [...current, routeId],
    );
  }

  async function onAssignRoutes(): Promise<void> {
    if (!session || !selectedPeriodId || !selectedDeviceCode) return;
    if (isSelectedPeriodClosed) {
      setError("El periodo seleccionado esta cerrado, no permite nuevas asignaciones.");
      setSuccess(null);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const currentRouteAssignments = assignments.filter(
        (assignment) =>
          assignment.readingPeriodId === selectedPeriodId &&
          assignment.assignedDeviceCode === selectedDeviceCode &&
          Boolean(assignment.routeId),
      );
      const currentRouteIds = new Set(currentRouteAssignments.map((assignment) => assignment.routeId));
      const nextRouteIds = new Set(selectedRouteIds);
      let assignedCount = 0;
      let unassignedCount = 0;

      for (const assignment of currentRouteAssignments) {
        if (!nextRouteIds.has(assignment.routeId)) {
          await deleteReadingAssignment(session, assignment.id);
          unassignedCount += 1;
        }
      }

      for (const routeId of selectedRouteIds) {
        if (currentRouteIds.has(routeId)) continue;
        await createReadingAssignment(session, {
          readingPeriodId: selectedPeriodId,
          routeId,
          assignedDeviceCode: selectedDeviceCode,
          assignmentStatus: "assigned",
        });
        assignedCount += 1;
      }
      await refreshOperational();
      if (assignedCount > 0 && unassignedCount > 0) {
        setSuccess(`Asignacion actualizada: ${assignedCount} ruta(s) asignada(s) y ${unassignedCount} desasignada(s).`);
      } else if (assignedCount > 0) {
        setSuccess(`Se asignaron ${assignedCount} ruta(s) correctamente.`);
      } else if (unassignedCount > 0) {
        setSuccess(`Se desasignaron ${unassignedCount} ruta(s) correctamente.`);
      } else {
        setSuccess("No hubo cambios en la asignacion.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron asignar rutas");
      setSuccess(null);
    } finally {
      setSaving(false);
    }
  }

  async function onSaveRoute(): Promise<void> {
    if (!session) return;
    if (!routeDraft.code.trim() || !routeDraft.name.trim()) {
      setError("La ruta debe tener codigo y nombre.");
      return;
    }
    if (routeDraft.sectorIds.length === 0) {
      setError("La ruta debe tener al menos un sector.");
      return;
    }
    setSavingRoute(true);
    setError(null);
    try {
      if (routeDraft.id) {
        await updateReadingRoute(session, routeDraft.id, {
          code: routeDraft.code.trim(),
          name: routeDraft.name.trim(),
          active: routeDraft.active,
          sectorIdsJson: routeDraft.sectorIds,
          contractIdsJson: routeDraft.contractIds,
        });
      } else {
        await createReadingRoute(session, {
          code: routeDraft.code.trim(),
          name: routeDraft.name.trim(),
          active: routeDraft.active,
          sectorIdsJson: routeDraft.sectorIds,
          contractIdsJson: routeDraft.contractIds,
        });
      }
      resetRouteDraft();
      setRoutes(await listReadingRoutes(session));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar la ruta");
    } finally {
      setSavingRoute(false);
    }
  }

  async function onDeleteRoute(routeId: string): Promise<void> {
    if (!session) return;
    setDeletingRouteId(routeId);
    setError(null);
    try {
      await deleteReadingRoute(session, routeId);
      if (routeDraft.id === routeId) resetRouteDraft();
      setRoutes(await listReadingRoutes(session));
      await refreshOperational();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo eliminar la ruta");
    } finally {
      setDeletingRouteId(null);
    }
  }

  async function onRelease(contractId: string): Promise<void> {
    if (!session || !selectedPeriodId) return;
    setReleasingContractId(contractId);
    setError(null);
    try {
      await releaseMeterReading(session, {
        readingPeriodId: selectedPeriodId,
        contractId,
      });
      await refreshOperational();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo liberar lectura");
    } finally {
      setReleasingContractId(null);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Control de Lecturas</h1>
          <p className="text-sm text-gray-500 mt-1">Asignacion de rutas y monitoreo del avance de lecturas.</p>
        </div>
        <button
          type="button"
          onClick={() => void refreshOperational()}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
        >
          {refreshing ? "Actualizando..." : "Actualizar ahora"}
        </button>
      </div>

      {error ? (
        <div className="p-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">{error}</div>
      ) : null}
      {success ? (
        <div className="p-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg">{success}</div>
      ) : null}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setActiveTab("asignacion")}
          className={`px-4 py-2 text-sm rounded-lg border ${
            activeTab === "asignacion"
              ? "bg-blue-600 text-white border-blue-600"
              : "bg-white text-gray-700 border-gray-200"
          }`}
        >
          Asignacion
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("rutas")}
          className={`px-4 py-2 text-sm rounded-lg border ${
            activeTab === "rutas" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-200"
          }`}
        >
          Rutas
        </button>
      </div>

      {activeTab === "asignacion" ? (
        <>
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700">Asignacion de rutas</h2>
            {isSelectedPeriodClosed ? (
              <div className="p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
                El periodo seleccionado esta cerrado. No se pueden asignar rutas a dispositivos.
              </div>
            ) : null}

            <div className="grid grid-cols-3 gap-3">
              <select
                value={selectedPeriodId}
                onChange={(event) => setSelectedPeriodId(event.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              >
                {periods.map((period) => (
                  <option key={period.id} value={period.id}>
                    {period.periodCode} ({period.status})
                  </option>
                ))}
              </select>
              <select
                value={selectedDeviceCode}
                onChange={(event) => setSelectedDeviceCode(event.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="">Selecciona dispositivo</option>
                {devices.map((device) => (
                  <option key={device.id} value={device.deviceCode}>
                    {device.deviceCode} - {device.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => void onAssignRoutes()}
                disabled={saving || isSelectedPeriodClosed || !selectedPeriodId || !selectedDeviceCode}
                className="px-3 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-60"
              >
                {saving ? "Guardando..." : "Guardar asignacion"}
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {routes.map((route) => {
                const active = selectedRouteIds.includes(route.id);
                return (
                  <button
                    key={route.id}
                    type="button"
                    onClick={() => toggleSelectedRoute(route.id)}
                    disabled={isSelectedPeriodClosed}
                    className={`px-3 py-1.5 text-xs rounded-full border disabled:opacity-50 ${
                      active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"
                    }`}
                  >
                    {route.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="text-xs text-gray-500">Cobertura</div>
              <div className="text-xl font-semibold text-gray-800">{routeCoverageEntries.length}</div>
              <div className="text-xs text-gray-500">Contratos en dispositivo/periodo</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="text-xs text-gray-500">Tomadas</div>
              <div className="text-xl font-semibold text-emerald-700">{tomadasCount}</div>
              <div className="text-xs text-gray-500">Lecturas sincronizadas</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="text-xs text-gray-500">Pendientes</div>
              <div className="text-xl font-semibold text-amber-700">{pendientesCount}</div>
              <div className="text-xs text-gray-500">Lecturas por tomar</div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
            <h2 className="text-sm font-semibold text-gray-700">Monitoreo en tiempo real</h2>
            <div className="grid grid-cols-3 gap-3">
              <select
                value={filterRoute}
                onChange={(event) => setFilterRoute(event.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="todos">Todas las rutas</option>
                {assignedRouteNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(event) => setFilterStatus(event.target.value as StatusFilter)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="todos">Todos</option>
                <option value="pendientes">Pendientes</option>
                <option value="tomadas">Tomadas</option>
              </select>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por cliente, RUT, contrato o direccion"
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />
            </div>

            <div className="space-y-2">
              {monitorRows.length === 0 ? (
                <p className="text-sm text-gray-500">Sin resultados para filtros aplicados.</p>
              ) : (
                monitorRows.map((row) => (
                  <div
                    key={row.contractId}
                    className="border border-gray-100 rounded-lg px-3 py-2 flex items-center justify-between"
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        {row.customerName} · {row.customerRut}
                      </div>
                      <div className="text-xs text-gray-500">
                        {row.customerAddress} · {row.routeLabel} · {row.contractNumber}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          row.status === "tomadas" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {row.status === "tomadas" ? "Tomada" : "Pendiente"}
                      </span>
                      {row.status === "tomadas" ? (
                        <button
                          type="button"
                          onClick={() => void onRelease(row.contractId)}
                          disabled={releasingContractId === row.contractId}
                          className="text-xs px-2 py-1 rounded-md border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-60"
                        >
                          {releasingContractId === row.contractId ? "Liberando..." : "Liberar"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-[1.1fr_1.4fr] gap-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-700">Rutas registradas</h2>
              <button
                type="button"
                onClick={resetRouteDraft}
                className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
              >
                Nueva ruta
              </button>
            </div>
            <input
              value={routeSearch}
              onChange={(event) => setRouteSearch(event.target.value)}
              placeholder="Buscar por codigo o nombre"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
            <div className="space-y-2 max-h-[620px] overflow-y-auto">
              {filteredRoutes.map((route) => {
                const sectorNames = route.sectorIds.map((sectorId) => sectorsById.get(sectorId)?.name ?? sectorId);
                return (
                  <div key={route.id} className="border border-gray-100 rounded-lg p-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-800">{route.name}</div>
                        <div className="text-xs text-gray-500">
                          {route.code} · {route.contractIds.length} contratos · {sectorNames.length} sectores
                        </div>
                      </div>
                      <span
                        className={`text-[11px] px-2 py-1 rounded-full ${
                          route.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {route.active ? "Activa" : "Inactiva"}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {sectorNames.map((name) => (
                        <span key={`${route.id}-${name}`} className="text-[11px] px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                          {name}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => startEditRoute(route)}
                        className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDeleteRoute(route.id)}
                        disabled={deletingRouteId === route.id}
                        className="px-3 py-2 text-xs border border-red-200 text-red-700 rounded-lg bg-red-50 hover:bg-red-100 disabled:opacity-60"
                      >
                        {deletingRouteId === route.id ? "Eliminando..." : "Eliminar"}
                      </button>
                    </div>
                  </div>
                );
              })}
              {filteredRoutes.length === 0 ? <div className="text-sm text-gray-500">No hay rutas registradas.</div> : null}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-700">
                  {routeDraft.id ? "Editar ruta" : "Nueva ruta"}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Una ruta puede mezclar uno o varios sectores y definir el orden real de lectura.
                </p>
              </div>
              {routeDraft.id ? (
                <button
                  type="button"
                  onClick={resetRouteDraft}
                  className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
                >
                  Cancelar edicion
                </button>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input
                value={routeDraft.code}
                onChange={(event) => setRouteDraft((current) => ({ ...current, code: event.target.value }))}
                placeholder="Codigo de ruta"
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />
              <input
                value={routeDraft.name}
                onChange={(event) => setRouteDraft((current) => ({ ...current, name: event.target.value }))}
                placeholder="Nombre de ruta"
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={routeDraft.active}
                onChange={(event) => setRouteDraft((current) => ({ ...current, active: event.target.checked }))}
              />
              Ruta activa
            </label>

            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">Sectores incluidos</div>
              <div className="flex flex-wrap gap-2">
                {sectors.map((sector) => {
                  const active = routeDraft.sectorIds.includes(sector.id);
                  return (
                    <button
                      key={sector.id}
                      type="button"
                      onClick={() => toggleRouteSector(sector.id)}
                      className={`px-3 py-1.5 text-xs rounded-full border ${
                        active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200"
                      }`}
                    >
                      {sector.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">Contratos disponibles</div>
                <div className="border border-gray-200 rounded-lg max-h-[360px] overflow-y-auto divide-y divide-gray-100">
                  {availableRouteContracts.map((contract) => {
                    const customerName = customersById.get(contract.customerId)?.nombre ?? "Sin cliente";
                    return (
                      <button
                        key={contract.id}
                        type="button"
                        onClick={() => addContractToRoute(contract.id)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50"
                      >
                        <div className="text-sm font-medium text-gray-800">{customerName}</div>
                        <div className="text-xs text-gray-500">
                          {contract.contractNumber} · {contract.sector} · {contract.startAddress}
                        </div>
                      </button>
                    );
                  })}
                  {availableRouteContracts.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-gray-500">Selecciona sectores para ver contratos.</div>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">Orden de la ruta</div>
                <div className="border border-gray-200 rounded-lg max-h-[360px] overflow-y-auto divide-y divide-gray-100">
                  {selectedRouteContracts.map((contract, index) => {
                    const customerName = customersById.get(contract.customerId)?.nombre ?? "Sin cliente";
                    const isDragging = draggingContractId === contract.id;
                    return (
                      <div
                        key={contract.id}
                        draggable
                        onDragStart={() => setDraggingContractId(contract.id)}
                        onDragEnd={() => setDraggingContractId(null)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          if (draggingContractId) {
                            reorderRouteContract(draggingContractId, contract.id);
                          }
                          setDraggingContractId(null);
                        }}
                        className={`px-3 py-2 space-y-2 cursor-move transition ${
                          isDragging ? "bg-blue-50 opacity-60" : "bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-medium text-gray-800">
                              {index + 1}. {customerName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {contract.contractNumber} · {contract.sector} · {contract.startAddress}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeContractFromRoute(contract.id)}
                            className="text-xs px-2 py-1 rounded-md border border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
                          >
                            Quitar
                          </button>
                        </div>
                        <div className="text-xs text-gray-400">Arrastra este cliente para cambiar el orden.</div>
                      </div>
                    );
                  })}
                  {selectedRouteContracts.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-gray-500">Agrega contratos para definir el orden.</div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => void onSaveRoute()}
                disabled={savingRoute}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-60"
              >
                {savingRoute ? "Guardando..." : routeDraft.id ? "Actualizar ruta" : "Crear ruta"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="text-xs text-gray-400">Cargando datos base...</div> : null}
    </div>
  );
}
